import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ArticleCardComponent, ArticleCardData, CardVariant } from '../article-card/article-card.component';
import { BlogListingService, ListingMode, SortOrder, BlogQueryOptions } from '../../services/blog-listing.service';
import { AudienceService } from '../../services/audience.service';
import { environment } from '../../../environments/environment';
import {
  dedupeArticleCardsByIdOrUrl,
  dedupeBlogEntriesById,
  getPublishedDateMsFromRaw,
} from '../../utils/blog-published-date';

/**
 * ArticleListingComponent - Reusable blog listing with pagination
 * 
 * Features:
 * - Configurable filtering (topic, content-series, subject, discipline)
 * - Dynamic dropdown selection in Builder.io (NEW!)
 * - Server-side pagination with count caching
 * - Page group navigation (show 5 pages at a time)
 * - Responsive card grid
 * - Optional URL state management
 * - Listens for filter change events from dropdown components
 * 
 * Usage in Builder.io:
 * 1. Drag component onto page
 * 2. Select mode (all, topic, content-series, subject, discipline)
 * 3. Choose category from dropdown (appears based on mode)
 *    - Mode "topic" → Shows topic dropdown
 *    - Mode "content-series" → Shows content series dropdown
 *    - Mode "subject" → Shows subject dropdown
 *    - Mode "discipline" → Shows discipline dropdown
 *    - Mode "all" → No dropdown, shows all articles
 * 4. Configure other options (basePath, cardVariant, etc.)
 * 5. Publish
 * 
 * Reference Fields (NEW in this version):
 * - selectedTopic: Builder.io reference to blog-topic model
 * - selectedContentSeries: Builder.io reference to content-series model
 * - selectedSubject: Builder.io reference to bfwsubject model
 * - selectedDiscipline: Builder.io reference to mlcourse model
 * 
 * These references are automatically mapped to filterId/filterValue internally.
 * The old filterId/filterValue inputs are still supported for backward compatibility.
 */
@Component({
  selector: 'app-article-listing',
  templateUrl: './article-listing.component.html',
  styleUrls: ['./article-listing.component.scss'],
  standalone: false
})
export class ArticleListingComponent implements OnInit, OnDestroy {
  // Configuration inputs (set in Builder.io)
  @Input() mode: ListingMode = 'all';
  @Input() filterId?: string;
  @Input() filterValue: string = 'All';
  @Input() basePath: string = '/content-hub';
  @Input() cardVariant: CardVariant = 'default';
  @Input() fallbackImages: string[] = [];
  @Input() listenToFilterEvents: boolean = true;
  
  // New reference-based inputs (Builder.io dropdowns)
  @Input() selectedTopic?: any;  // Reference to blog-topic
  @Input() selectedContentSeries?: any;  // Reference to content-series
  @Input() selectedSubject?: any;  // Reference to bfwsubject
  @Input() selectedDiscipline?: any;  // Reference to mlcourse
  
  // Fixed configuration values (not configurable in Builder.io)
  private readonly itemsPerPage: number = 21;
  private readonly pagesPerGroup: number = 5;
  private readonly sortOrder: SortOrder = 'newest';
  readonly updateUrl: boolean = true; // Public because used in template
  private readonly showFeaturedImageFallback: boolean = true;
  
  // Component state
  articles: ArticleCardData[] = [];
  allArticles: ArticleCardData[] = []; // Store all loaded articles for client-side filtering
  filteredArticles: ArticleCardData[] = []; // Filtered articles (before pagination)
  currentPage: number = 1;
  totalPages: number = 0;
  totalCount: number = 0;
  pageGroupStart: number = 1;
  pageNumbers: number[] = [];
  loading: boolean = false;
  error: string | null = null;
  private isInitialLoad: boolean = true; // Track if this is the first load
  
  // Client-side filter state
  filterOptions = {
    disciplines: [] as string[],
    subjects: [] as string[],
    topics: [] as string[],
    themes: [] as string[],
    contentTypes: [] as string[],
    contentSeries: [] as string[],
    products: [] as string[]
  };
  
  // Store initial filter options (for cascading logic)
  private initialFilterOptions = {
    disciplines: [] as string[],
    subjects: [] as string[],
    topics: [] as string[],
    themes: [] as string[],
    contentTypes: [] as string[],
    contentSeries: [] as string[],
    products: [] as string[]
  };
  
  selectedFilters = {
    discipline: 'All',
    subject: 'All',
    topic: 'All',
    theme: 'All',
    contentType: 'All',
    contentSeries: 'All',
    product: 'All'
  };
  
  // Dynamically fetched fallback images from Builder.io config
  // Store as objects to preserve both URL and altText metadata
  private dynamicFallbackImages: Array<{ url: string; altText?: string }> = [];
  
  // Fallback image URLs (string array for backward compatibility with @Input)
  private fallbackImageUrls: string[] = [];

  /** Incremented on every loadArticles() call. Lets us discard results from superseded loads. */
  private currentLoadId = 0;

  /** AbortController for the currently active loadArticles() call. */
  private activeLoadController: AbortController | null = null;
  
  private destroy$ = new Subject<void>();
  
  constructor(
    private blogService: BlogListingService,
    private audienceService: AudienceService,
    private route: ActivatedRoute,
    private router: Router
  ) {}
  
  async ngOnInit() {
    // Map reference-based inputs to filterId and filterValue
    this.mapReferenceInputsToFilter();
    
    // Initialize fallback images
    await this.loadFallbackImagesFromFolder();
    
    // Use dynamic fallbacks if available, otherwise use input or hardcoded
    if (this.dynamicFallbackImages.length === 0) {
      if (this.fallbackImages && this.fallbackImages.length > 0) {
        // Convert input string URLs to objects
        this.fallbackImageUrls = this.fallbackImages;
      } else {
        // Use hardcoded fallbacks
        this.fallbackImageUrls = this.getDefaultFallbackImages();
      }
    }
    
    // Listen for filter changes from dropdown (if enabled)
    if (this.listenToFilterEvents) {
      this.setupFilterListener();
    }
    
    // Read filters and page from URL if updateUrl is enabled
    if (this.updateUrl) {
      this.route.queryParams.pipe(
        takeUntil(this.destroy$)
      ).subscribe(params => {
        this.readFiltersFromUrl(params);
      });
    }
    
    // Initial load
    await this.loadArticles();
  }
  
  ngOnDestroy() {
    // Invalidate any in-flight loadArticles() call so its await chains
    // do not mutate state on a destroyed component instance.
    this.currentLoadId++;
    if (this.activeLoadController) {
      this.activeLoadController.abort();
      this.activeLoadController = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  /**
   * Map reference-based inputs (from Builder.io dropdowns) to filterId and filterValue
   * This allows backward compatibility while providing better UX in Builder.io
   */
  private mapReferenceInputsToFilter(): void {
    // If filterId is already set, don't override (backward compatibility)
    if (this.filterId) {
      return;
    }
    
    let selectedRef: any = null;
    let nameField = 'name';  // Default field name
    
    // Determine which reference to use based on mode
    switch (this.mode) {
      case 'topic':
        selectedRef = this.selectedTopic;
        break;
      case 'content-series':
        selectedRef = this.selectedContentSeries;
        break;
      case 'subject':
        selectedRef = this.selectedSubject;
        break;
      case 'discipline':
        selectedRef = this.selectedDiscipline;
        nameField = 'coursename';  // Discipline uses 'coursename' instead of 'name'
        break;
    }
    
    // Extract ID and name from the reference
    if (selectedRef) {
      // Handle both string ID and object reference formats
      if (typeof selectedRef === 'string') {
        // If it's just an ID string
        this.filterId = selectedRef;
        console.log(`📌 Using reference ID: ${this.filterId}`);
      } else if (selectedRef && typeof selectedRef === 'object') {
        // If it's a full Builder.io reference object
        // Try multiple access patterns based on Builder.io's structure
        const refId = selectedRef.id || selectedRef.value?.id || selectedRef.value?.data?.id;
        const refName = selectedRef.data?.[nameField] || 
                       selectedRef.value?.data?.[nameField] || 
                       selectedRef.data?.name || 
                       selectedRef.value?.data?.name ||
                       selectedRef.name;
        
        if (refId) {
          this.filterId = refId;
          console.log(`📌 Using reference ID from object: ${this.filterId}`);
        }
        
        if (refName && this.filterValue === 'All') {
          this.filterValue = refName;
          console.log(`📌 Using reference name: ${this.filterValue}`);
        }
      }
    }
    
    // Log the final configuration
    console.log(`🔧 Article Listing Config: mode="${this.mode}", filterId="${this.filterId || 'none'}", filterValue="${this.filterValue}"`);
  }
  
  /**
   * Listen for custom filter change events from dropdown components
   */
  private setupFilterListener(): void {
    if (typeof window === 'undefined') return;
    
    const eventNames = ['filterChanged', 'bfwTopicSelected'];
    
    eventNames.forEach(eventName => {
      const handler = (event: Event) => {
        const customEvent = event as CustomEvent;
        const { id, name, mode } = customEvent.detail || {};

        // Embedded Builder/HTML dropdowns often dispatch { id: 'ALL', name: 'All' } on init to
        // "sync" state. That would wipe a Builder-configured filter and reload with $exists
        // (all entries that have any content series). Ignore that broadcast until initial
        // load finishes — real user "clear filter" actions happen after load.
        if ((!id || id === 'ALL') && this.isInitialLoad) {
          return;
        }
        
        // Update filter
        this.filterId = (!id || id === 'ALL') ? undefined : id;
        this.filterValue = name || 'All';
        if (mode && mode !== this.mode) {
          this.mode = mode;
        }
        
        // Reset pagination and reload
        // IMPORTANT: Skip pagination reset on initial load if URL has page param
        // This allows URL-based pagination to work correctly with filter events
        if (!this.isInitialLoad) {
          this.resetPagination();
        }
        this.loadArticles();
      };
      
      window.addEventListener(eventName, handler);
      
      // Cleanup on destroy
      this.destroy$.subscribe(() => {
        window.removeEventListener(eventName, handler);
      });
    });
  }
  
  /**
   * Load all articles matching initial configuration (optimized with parallel fetching)
   */
  async loadArticles(): Promise<void> {
    // Abort any previous in-flight requests before starting a new load
    if (this.activeLoadController) {
      this.activeLoadController.abort();
    }
    this.activeLoadController = new AbortController();
    const signal = this.activeLoadController.signal;

    const loadId = ++this.currentLoadId;
    this.loading = true;
    this.error = null;
    
    try {
      const queryOptions: BlogQueryOptions = {
        mode: this.mode,
        filterId: this.filterId,
        filterValue: this.filterValue,
        sortOrder: this.sortOrder,
        signal
      };
      
      console.log('🚀 Loading articles with optimized parallel fetching...');
      const startTime = Date.now();
      
      // Fetch first batch to check how many articles exist
      const firstBatch = await this.blogService.fetchBlogs({
        ...queryOptions,
        limit: 100,
        offset: 0
      });

      // A newer load has started while we were awaiting — discard these results
      if (loadId !== this.currentLoadId) {
        console.log(`🚫 Load ${loadId} superseded by load ${this.currentLoadId}, discarding first-batch results`);
        return;
      }
      
      console.log(`📊 First batch: ${firstBatch.results.length} articles (api full page: ${firstBatch.apiReturnedFullPage})`);
      
      let resolvedArticles: any[];

      // If the API did not return a full page, there are no more offsets to fetch
      // (do not use results.length — dedupe/sort can shrink below 100 while more pages exist)
      if (!firstBatch.apiReturnedFullPage) {
        resolvedArticles = firstBatch.results;
        console.log(`⚡ Loaded all ${resolvedArticles.length} articles in ${((Date.now() - startTime) / 1000).toFixed(2)}s`);
      } else {
        // Need to fetch more — use smart estimation
        resolvedArticles = await this.fetchAllArticlesInBatches(queryOptions, firstBatch, loadId);

        // A newer load may have started during the multi-batch fetch
        if (loadId !== this.currentLoadId) {
          console.log(`🚫 Load ${loadId} superseded by load ${this.currentLoadId}, discarding batch results`);
          return;
        }
        
        const loadTime = ((Date.now() - startTime) / 1000).toFixed(2);
        console.log(`⚡ Loaded all ${resolvedArticles.length} articles in ${loadTime}s`);
      }
      
      this.allArticles = resolvedArticles.map(article => this.mapArticleToCard(article));

      // Sort articles by published date (newest first) to ensure correct order
      this.sortArticlesByDate();
      this.allArticles = dedupeArticleCardsByIdOrUrl(this.allArticles);

      // Extract filter options and store initial options for cascading
      this.extractFilterOptions(this.allArticles);
      this.storeInitialFilterOptions();
      
      // Apply filters (will cascade options if filters are active)
      this.applyClientFilters();
      
      // Mark initial load as complete after first article load
      if (this.isInitialLoad) {
        this.isInitialLoad = false;
      }
      
    } catch (error: any) {
      // AbortError means this load was intentionally cancelled (superseded or destroyed) — no-op
      if (error?.name === 'AbortError') return;
      if (loadId !== this.currentLoadId) return;
      console.error('Error loading articles:', error);
      this.error = 'Failed to load articles. Please try again.';
      this.articles = [];
    } finally {
      if (loadId === this.currentLoadId) {
        this.loading = false;
      }
    }
  }
  
  /**
   * Fetch all articles in batches (OPTIMIZED: Parallel fetching with smart estimation)
   * Builder.io limits responses to 100 per request
   * 
   * Strategy:
   * 1. Start with first batch (already fetched)
   * 2. Fetch next few batches in parallel
   * 3. If all batches return 100 results, fetch more batches
   * 4. Stop when we get a batch with < 100 results
   */
  private async fetchAllArticlesInBatches(
    queryOptions: BlogQueryOptions,
    firstBatch: any,
    loadId: number
  ): Promise<any[]> {
    const batchSize = 100;
    const allResults: any[] = [...firstBatch.results];
    
    // Start with estimating we need 5 batches (500 articles)
    // We'll adjust if needed
    let currentOffset = batchSize;
    let keepFetching = true;
    
    while (keepFetching) {
      // Stop fetching if a newer load has superseded this one
      if (loadId !== this.currentLoadId) {
        console.log(`🚫 Load ${loadId} superseded mid-batch, stopping further fetches`);
        return [];
      }

      // Fetch next 4 batches in parallel
      const batchPromises: Promise<any>[] = [];
      const offsets: number[] = [];
      
      for (let i = 0; i < 4; i++) {
        const offset = currentOffset + (i * batchSize);
        offsets.push(offset);
        batchPromises.push(
          this.blogService.fetchBlogs({
            ...queryOptions,
            limit: batchSize,
            offset: offset
          })
        );
      }
      
      console.log(`🔄 Fetching batches at offsets: ${offsets.join(', ')}`);
      
      // Wait for all parallel batches
      const batchResults = await Promise.all(batchPromises);
      
      // Add results IN ORDER (maintain sort order from API)
      let foundIncompleteRatch = false;
      for (let i = 0; i < batchResults.length; i++) {
        const result = batchResults[i];
        if (result.results.length > 0) {
          allResults.push(...result.results);
        }
        
        // Use API page fullness, not deduped length — sort/dedupe can shrink a batch below 100
        // even when Builder returned a full page and more offsets exist.
        if (!result.apiReturnedFullPage) {
          foundIncompleteRatch = true;
          keepFetching = false;
          break;
        }
      }
      
      // If all 4 batches returned 100, we might need more
      if (!foundIncompleteRatch) {
        currentOffset += (4 * batchSize);
      }
      
      // Hard safety: avoid runaway loops if the API misbehaves (~50k articles)
      const maxOffset = 50000;
      if (currentOffset >= maxOffset) {
        console.warn(`⚠️ Reached ${maxOffset} offset safety limit, stopping fetch`);
        keepFetching = false;
      }
    }
    
    console.log(`📦 Total batches fetched: ${Math.ceil(allResults.length / batchSize)}`);

    return dedupeBlogEntriesById(allResults) as any[];
  }
  
  /**
   * Human-readable label from a Builder reference's resolved `value`.
   * Prefer `value.data` keys (e.g. name, coursename); fall back to top-level `value.name`
   * when data is missing or does not duplicate the entry title (common for blog-themes).
   */
  private refValueDisplayName(ref: any, ...dataKeys: string[]): string {
    const v = ref?.value;
    if (!v) return '';
    const d = v.data;
    if (d && dataKeys.length) {
      for (const key of dataKeys) {
        const s = d[key];
        if (typeof s === 'string' && s.trim()) {
          return s.trim();
        }
      }
    }
    if (typeof v.name === 'string' && v.name.trim()) {
      return v.name.trim();
    }
    return '';
  }

  /**
   * Map Builder.io article to card display data
   */
  private mapArticleToCard(article: any): ArticleCardData {
    try {
      const data = article?.data || {};
      
      // Get featured image with fallback (pass article ID for consistent fallback selection)
      const imageResult = this.getFeaturedImageWithAlt(data, article.id);
      
      // Get author data
      const author = {
        name: this.refValueDisplayName(data.authors, 'name') || 'Anonymous',
        avatarUrl: data.authors?.value?.data?.headshot || ''
      };
      
      // Build article URL
      const slug = data.slug || '';
      const audience = this.audienceService.audience || 'highered';
      const prefix = slug.startsWith('/ref/') ? 'ref' : 
                     slug.startsWith('/post/') ? 'post' : 'ref';
      const cleanSlug = slug.replace(/^\/?(ref|post)\//, '');
      const url = `${this.basePath}/${audience}/${prefix}/${cleanSlug}`;
      
      // Strip HTML from excerpt
      const excerpt = this.stripHtml(data.excerpt || '');
      
      // Extract taxonomy data for filtering (data.* first, then value.name as Builder backup)
      const discipline = this.refValueDisplayName(data.discipline, 'coursename', 'name');
      const subject = this.refValueDisplayName(data.subject, 'name');
      const contentSeries = this.refValueDisplayName(data.contentSeries, 'name');
      const contentType = data.contentType || '';
      
      // Extract topics and themes from topicthemepairs
      const topics: string[] = [];
      const themes: string[] = [];
      if (data.topicthemepairs && Array.isArray(data.topicthemepairs)) {
        data.topicthemepairs.forEach((pair: any) => {
          const topicName = this.refValueDisplayName(pair.topic, 'name');
          const themeName = this.refValueDisplayName(pair.theme, 'name');
          if (topicName && !topics.includes(topicName)) {
            topics.push(topicName);
          }
          if (themeName && !themes.includes(themeName)) {
            themes.push(themeName);
          }
        });
      }
      
      // Extract products (if exists as array)
      const products: string[] = [];
      if (data.products && Array.isArray(data.products)) {
        data.products.forEach((prod: any) => {
          const prodName =
            this.refValueDisplayName(prod, 'name') ||
            (typeof prod?.name === 'string' && prod.name.trim() ? prod.name.trim() : '');
          if (prodName && !products.includes(prodName)) {
            products.push(prodName);
          }
        });
      } else if (data.product) {
        // Single product field
        const prodName =
          this.refValueDisplayName(data.product, 'name') ||
          (typeof data.product === 'string' ? data.product : '');
        if (prodName) products.push(prodName);
      }
      
      return {
        id: article.id || 'unknown',
        title: data.title || 'Untitled',
        excerpt: excerpt,
        imageUrl: imageResult.url,
        imageAlt: imageResult.alt,  // Use specific alt text (descriptive for fallbacks)
        publishedDate: data.publishedDate || '',
        author: author,
        url: url,
        discipline: discipline,
        subject: subject,
        topics: topics,
        themes: themes,
        contentType: contentType,
        contentSeries: contentSeries,
        products: products
      };
    } catch (error) {
      console.error('❌ Error mapping article:', article, error);
      return {
        id: article?.id || 'error',
        title: 'Error loading article',
        excerpt: '',
        imageUrl: 'https://placehold.co/600x400?text=Error',
        imageAlt: 'Error loading article image',
        publishedDate: '',
        author: { name: 'Unknown', avatarUrl: '' },
        url: '#'
      };
    }
  }
  
  /**
   * Sort articles by published date (newest first)
   * This ensures correct order even if parallel batch fetching causes any issues
   */
  private sortArticlesByDate(): void {
    this.allArticles.sort((a, b) => {
      const dateA = getPublishedDateMsFromRaw(a.publishedDate);
      const dateB = getPublishedDateMsFromRaw(b.publishedDate);
      const primary = dateB - dateA;
      if (primary !== 0) {
        return primary;
      }
      return String(a.id).localeCompare(String(b.id));
    });

    console.log('✅ Articles sorted by date (newest first)');
  }
  
  /**
   * Extract unique filter options from loaded articles
   */
  private extractFilterOptions(articles: ArticleCardData[]): void {
    const disciplines = new Set<string>();
    const subjects = new Set<string>();
    const topics = new Set<string>();
    const themes = new Set<string>();
    const contentTypes = new Set<string>();
    const contentSeries = new Set<string>();
    const products = new Set<string>();
    
    articles.forEach(article => {
      if (article.discipline) disciplines.add(article.discipline);
      if (article.subject) subjects.add(article.subject);
      if (article.contentType) contentTypes.add(article.contentType);
      if (article.contentSeries) contentSeries.add(article.contentSeries);
      
      article.topics?.forEach(topic => {
        if (topic) topics.add(topic);
      });
      article.themes?.forEach(theme => {
        if (theme) themes.add(theme);
      });
      article.products?.forEach(product => {
        if (product) products.add(product);
      });
    });
    
    this.filterOptions = {
      disciplines: Array.from(disciplines).sort(),
      subjects: Array.from(subjects).sort(),
      topics: Array.from(topics).sort(),
      themes: Array.from(themes).sort(),
      contentTypes: Array.from(contentTypes).sort(),
      contentSeries: Array.from(contentSeries).sort(),
      products: Array.from(products).sort()
    };
  }
  
  /**
   * Store initial filter options (from all articles) for reference
   * Used to preserve options for the initial filter mode
   */
  private storeInitialFilterOptions(): void {
    this.initialFilterOptions = {
      disciplines: [...this.filterOptions.disciplines],
      subjects: [...this.filterOptions.subjects],
      topics: [...this.filterOptions.topics],
      themes: [...this.filterOptions.themes],
      contentTypes: [...this.filterOptions.contentTypes],
      contentSeries: [...this.filterOptions.contentSeries],
      products: [...this.filterOptions.products]
    };
  }
  
  /**
   * Apply client-side filters to articles (strict filtering with cascading)
   * Only show articles that have the selected taxonomy values
   * After filtering, re-extract available filter options from filtered results
   */
  private applyClientFilters(): void {
    let filtered = [...this.allArticles];
    
    // Apply filters - only include articles that have matching values
    if (this.selectedFilters.discipline !== 'All') {
      filtered = filtered.filter(a => a.discipline === this.selectedFilters.discipline);
    }
    
    if (this.selectedFilters.subject !== 'All') {
      filtered = filtered.filter(a => a.subject === this.selectedFilters.subject);
    }
    
    if (this.selectedFilters.topic !== 'All') {
      filtered = filtered.filter(a => a.topics && a.topics.length > 0 && a.topics.includes(this.selectedFilters.topic));
    }
    
    if (this.selectedFilters.theme !== 'All') {
      filtered = filtered.filter(a => a.themes && a.themes.length > 0 && a.themes.includes(this.selectedFilters.theme));
    }
    
    if (this.selectedFilters.contentType !== 'All') {
      filtered = filtered.filter(a => a.contentType === this.selectedFilters.contentType);
    }
    
    if (this.selectedFilters.contentSeries !== 'All') {
      filtered = filtered.filter(a => a.contentSeries === this.selectedFilters.contentSeries);
    }
    
    if (this.selectedFilters.product !== 'All') {
      filtered = filtered.filter(a => a.products && a.products.length > 0 && a.products.includes(this.selectedFilters.product));
    }
    
    // Update filtered results
    this.filteredArticles = filtered;
    
    // ✨ CASCADING FILTERS: Re-extract filter options from filtered results
    // This makes other dropdowns show only values available in the current filtered set
    this.extractFilterOptions(filtered);
    
    // Preserve initial filter dropdown values based on mode
    // If page loaded with specific mode (e.g., all "Biology" articles),
    // keep showing all options for that taxonomy so user can switch
    this.preserveInitialModeOptions();
    
    // Validate selected filters - clear any that are no longer available
    this.validateSelectedFilters();
    
    // Update pagination
    this.totalCount = this.filteredArticles.length;
    this.totalPages = Math.ceil(this.totalCount / this.itemsPerPage);
    this.updatePaginatedArticles();
  }
  
  /**
   * Preserve filter options for the initial mode
   * Example: If page loaded with mode='discipline', keep all disciplines visible
   * so user can switch between disciplines
   */
  private preserveInitialModeOptions(): void {
    switch (this.mode) {
      case 'discipline':
        // Keep all disciplines visible (from initial load)
        this.filterOptions.disciplines = [...this.initialFilterOptions.disciplines];
        break;
      case 'subject':
        // Keep all subjects visible
        this.filterOptions.subjects = [...this.initialFilterOptions.subjects];
        break;
      case 'topic':
        // Keep all topics visible
        this.filterOptions.topics = [...this.initialFilterOptions.topics];
        break;
      case 'content-series':
        // Keep all content series visible
        this.filterOptions.contentSeries = [...this.initialFilterOptions.contentSeries];
        break;
    }
  }
  
  /**
   * Validate that selected filters are still available after cascading
   * If a selected filter value no longer exists in the filtered results,
   * clear it and re-apply filters
   */
  private validateSelectedFilters(): void {
    let needsRefilter = false;
    
    // Check each selected filter
    if (this.selectedFilters.discipline !== 'All' && 
        !this.filterOptions.disciplines.includes(this.selectedFilters.discipline)) {
      this.selectedFilters.discipline = 'All';
      needsRefilter = true;
    }
    
    if (this.selectedFilters.subject !== 'All' && 
        !this.filterOptions.subjects.includes(this.selectedFilters.subject)) {
      this.selectedFilters.subject = 'All';
      needsRefilter = true;
    }
    
    if (this.selectedFilters.topic !== 'All' && 
        !this.filterOptions.topics.includes(this.selectedFilters.topic)) {
      this.selectedFilters.topic = 'All';
      needsRefilter = true;
    }
    
    if (this.selectedFilters.theme !== 'All' && 
        !this.filterOptions.themes.includes(this.selectedFilters.theme)) {
      this.selectedFilters.theme = 'All';
      needsRefilter = true;
    }
    
    if (this.selectedFilters.contentType !== 'All' && 
        !this.filterOptions.contentTypes.includes(this.selectedFilters.contentType)) {
      this.selectedFilters.contentType = 'All';
      needsRefilter = true;
    }
    
    if (this.selectedFilters.contentSeries !== 'All' && 
        !this.filterOptions.contentSeries.includes(this.selectedFilters.contentSeries)) {
      this.selectedFilters.contentSeries = 'All';
      needsRefilter = true;
    }
    
    if (this.selectedFilters.product !== 'All' && 
        !this.filterOptions.products.includes(this.selectedFilters.product)) {
      this.selectedFilters.product = 'All';
      needsRefilter = true;
    }
    
    // If any filter was cleared, log a warning
    if (needsRefilter) {
      console.log('⚠️ Some filters were cleared because they are no longer available in filtered results');
    }
  }
  
  /**
   * Update displayed articles from filtered results (client-side pagination)
   */
  private updatePaginatedArticles(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.articles = this.filteredArticles.slice(startIndex, endIndex);
    this.updatePageNumbers();
    
    // Log pagination info to console
    console.log(`📄 Pagination: Page ${this.currentPage} of ${this.totalPages} (${this.totalCount} total articles)`);
  }
  
  /**
   * Handle filter change from dropdown
   */
  onFilterChange(): void {
    this.currentPage = 1;
    this.pageGroupStart = 1;
    this.applyClientFilters();
    this.updateUrlWithFilters();
  }
  
  /**
   * Clear all client-side filters
   */
  clearAllFilters(): void {
    this.selectedFilters = {
      discipline: 'All',
      subject: 'All',
      topic: 'All',
      theme: 'All',
      contentType: 'All',
      contentSeries: 'All',
      product: 'All'
    };
    
    // Reset to showing all articles and all filter options
    this.extractFilterOptions(this.allArticles);
    this.storeInitialFilterOptions();
    
    this.onFilterChange();
  }
  
  /**
   * Read filter states from URL query parameters
   */
  private readFiltersFromUrl(params: any): void {
    let hasChanges = false;
    
    // Read page number
    const page = parseInt(params['page'] || '1', 10);
    if (page !== this.currentPage && page >= 1) {
      this.currentPage = page;
      // Calculate the correct page group start for this page
      this.pageGroupStart = Math.floor((page - 1) / this.pagesPerGroup) * this.pagesPerGroup + 1;
      hasChanges = true;
    }
    
    // Read filter values
    const urlFilters = {
      discipline: params['discipline'] || 'All',
      subject: params['subject'] || 'All',
      topic: params['topic'] || 'All',
      theme: params['theme'] || 'All',
      contentType: params['contentType'] || 'All',
      contentSeries: params['contentSeries'] || 'All',
      product: params['product'] || 'All'
    };
    
    // Check if filters have changed
    if (JSON.stringify(urlFilters) !== JSON.stringify(this.selectedFilters)) {
      this.selectedFilters = urlFilters;
      hasChanges = true;
    }
    
    // Only reload if something changed (avoid infinite loop)
    if (hasChanges && this.allArticles.length > 0) {
      this.applyClientFilters();
    }
  }
  
  /**
   * Update URL with current filter states
   */
  private updateUrlWithFilters(): void {
    if (!this.updateUrl) return;
    
    const queryParams: any = {
      page: this.currentPage
    };
    
    // Add active filters to URL
    if (this.selectedFilters.discipline !== 'All') {
      queryParams.discipline = this.selectedFilters.discipline;
    }
    if (this.selectedFilters.subject !== 'All') {
      queryParams.subject = this.selectedFilters.subject;
    }
    if (this.selectedFilters.topic !== 'All') {
      queryParams.topic = this.selectedFilters.topic;
    }
    if (this.selectedFilters.theme !== 'All') {
      queryParams.theme = this.selectedFilters.theme;
    }
    if (this.selectedFilters.contentType !== 'All') {
      queryParams.contentType = this.selectedFilters.contentType;
    }
    if (this.selectedFilters.contentSeries !== 'All') {
      queryParams.contentSeries = this.selectedFilters.contentSeries;
    }
    if (this.selectedFilters.product !== 'All') {
      queryParams.product = this.selectedFilters.product;
    }
    
    // Replace all query parameters (not merge) to ensure removed filters are cleared from URL
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams
    });
  }
  
  /**
   * Check if any filters are active
   */
  get hasActiveFilters(): boolean {
    return Object.values(this.selectedFilters).some(v => v !== 'All');
  }
  
  /**
   * Get featured image with alt text for accessibility
   * Extracts altText from the file object if available
   * 
   * NEW DATA STRUCTURE (v2.1.1):
   * - featuredImage is now an Object with nested 'altImage' (File type)
   * - altImage contains: { url: string, altText: string, ... }
   * 
   * Priority: 
   * 1. featuredImage.altImage (new nested structure)
   * 2. featuredImage direct file (legacy)
   * 3. Fallback images → Placeholder
   */
  private getFeaturedImageWithAlt(data: any, articleId?: string): { url: string; alt: string } {
    const featured = data?.featuredImage;
    const articleTitle = data?.title || 'Article';
    
    // 1. NEW STRUCTURE: Check for nested altImage (Object with File field)
    // Structure: { altImage: "url" OR {url, altText}, altText: "text" }
    // altText can be at parent level (featuredImage.altText) or nested (altImage.altText)
    if (featured && typeof featured === 'object' && featured.altImage) {
      const altImage = featured.altImage;
      
      // Get altText from parent object if available
      const parentAltText = featured.altText;
      
      // altImage can be a direct URL string
      if (typeof altImage === 'string' && altImage.trim()) {
        return {
          url: altImage,
          alt: parentAltText || articleTitle  // Use parent altText if available
        };
      }
      
      // altImage can be an object with url and potentially altText
      if (typeof altImage === 'object' && altImage.url) {
        // Priority: nested altText > parent altText > article title
        const altText = altImage.altText || parentAltText || articleTitle;
        return {
          url: altImage.url,
          alt: altText
        };
      }
    }
    
    // 2. LEGACY: Check if featuredImage is a direct string URL (simple file field)
    if (typeof featured === 'string' && featured.trim()) {
      return { 
        url: featured,
        alt: articleTitle  // No altText available for string URLs, use title
      };
    }
    
    // 3. LEGACY: Check if featuredImage is an object with 'url' property (file type object)
    if (featured && typeof featured === 'object' && featured.url) {
      return {
        url: featured.url,
        alt: featured.altText || articleTitle  // Use file's altText if available
      };
    }
    
    // 4. LEGACY: Check if featuredImage is a reference field (legacy structure)
    if (featured?.value?.data?.image) {
      return {
        url: featured.value.data.image,
        alt: featured.value.data.altText || articleTitle  // Check for altText in nested structure
      };
    }
    
    // 5. LEGACY: Check for nested url in value (alternative structure)
    if (featured?.value?.url) {
      return {
        url: featured.value.url,
        alt: featured.value.altText || articleTitle  // Check for altText in value
      };
    }
    
    // 6. Fallback to consistent image based on article ID (if enabled)
    if (this.showFeaturedImageFallback) {
      // Priority 1: Dynamic fallback images from Builder.io (with altText)
      if (this.dynamicFallbackImages && this.dynamicFallbackImages.length > 0) {
        if (articleId) {
          const hash = this.hashCode(articleId);
          const consistentIndex = Math.abs(hash) % this.dynamicFallbackImages.length;
          const fallbackItem = this.dynamicFallbackImages[consistentIndex];
          return {
            url: fallbackItem.url,
            alt: fallbackItem.altText || 'Decorative image for article'  // ✅ Use altText from Builder.io!
          };
        }
        // Random fallback
        const randomIndex = Math.floor(Math.random() * this.dynamicFallbackImages.length);
        const fallbackItem = this.dynamicFallbackImages[randomIndex];
        return {
          url: fallbackItem.url,
          alt: fallbackItem.altText || 'Decorative image for article'
        };
      }
      
      // Priority 2: Hardcoded fallback URLs (no altText available)
      if (this.fallbackImageUrls && this.fallbackImageUrls.length > 0) {
        if (articleId) {
          const hash = this.hashCode(articleId);
          const consistentIndex = Math.abs(hash) % this.fallbackImageUrls.length;
          return {
            url: this.fallbackImageUrls[consistentIndex],
            alt: 'Decorative image for article'  // Generic alt for hardcoded images
          };
        }
        const randomIndex = Math.floor(Math.random() * this.fallbackImageUrls.length);
        return {
          url: this.fallbackImageUrls[randomIndex],
          alt: 'Decorative image for article'
        };
      }
    }
    
    // 6. Last resort: placeholder image
    return {
      url: 'https://placehold.co/600x400?text=No+Image',
      alt: 'Placeholder image - no featured image available'
    };
  }
  
  /**
   * Simple hash function to convert string to number
   * Used for consistent fallback image selection
   */
  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash;
  }
  
  /**
   * Get default fallback images (hardcoded - used as last resort)
   */
  private getDefaultFallbackImages(): string[] {
    return [
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F4f7f666fd5f9483abb5e8bf66a69be24",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2Fc151390f9ea44861b1b4619154c84025",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F265b53f04d2c4f7a9c6839eae1efb7f9",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F074a34ef88bc4aa88950d62ee4dd091f",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F56383631a9ab415a9f1cfeb42abf132b",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F3f2252ffc95a4c1d854bdc7d210df290",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2Fc6fc4fa3f18742b887b07270cd8cb0f7",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F6c89f91bbbf2465d92733cae60a99525",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F78c3969726214512b450f749dc691913",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F731c2ad7d4d1455db70112b5bc885afe"
    ];
  }
  
  /**
   * Load fallback images from Builder.io config
   */
  private async loadFallbackImagesFromFolder(): Promise<void> {
    try {
      const apiKey = environment.builderApiKey;
      const url = `https://cdn.builder.io/api/v3/content/fallback-images-config?apiKey=${apiKey}&query.id=986366fbe186497a8a70135c52243bdc&limit=1`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.results?.[0]?.data?.images) {
        this.dynamicFallbackImages = data.results[0].data.images
          .map((img: any) => {
            let url: string | null = null;
            let altText: string | undefined = undefined;
            
            // Handle direct string URL (no altText available)
            if (typeof img === 'string') {
              return { url: img, altText: undefined };
            }
            
            // Handle new structure: { image: { altImage: "url", altText: "text" } }
            if (img?.image) {
              // Extract altText from parent image object
              altText = img.image.altText;
              
              // Extract URL from altImage
              if (img.image.altImage) {
                // altImage can be a string URL
                if (typeof img.image.altImage === 'string') {
                  url = img.image.altImage;
                }
                // or an object with url property
                else if (typeof img.image.altImage === 'object' && img.image.altImage.url) {
                  url = img.image.altImage.url;
                  // Also check for nested altText (file metadata)
                  if (!altText && img.image.altImage.altText) {
                    altText = img.image.altImage.altText;
                  }
                }
              }
              // Legacy: { image: "url" }
              else if (typeof img.image === 'string') {
                url = img.image;
              }
            }
            
            // Legacy: { url: "url", altText: "text" }
            if (!url && typeof img?.url === 'string') {
              url = img.url;
              if (!altText) altText = img.altText;
            }
            
            return url ? { url, altText } : null;
          })
          .filter((item: any) => item !== null);
        
        if (this.dynamicFallbackImages.length > 0) {
          console.log(`✅ Loaded ${this.dynamicFallbackImages.length} fallback images from Builder.io with altText metadata`);
        }
      }
    } catch (error) {
      // Silently fail - will use hardcoded fallbacks
    }
  }
  
  /**
   * Strip HTML from Builder.io excerpt/rich text to plain text for safe display.
   * Do not assign remote HTML to innerHTML (DOM XSS); use string stripping only (browser + SSR).
   */
  private stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  }
  
  /**
   * Pagination methods
   */
  goToPage(page: number, event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    
    this.currentPage = page;
    
    // Update URL with current page and filters
    this.updateUrlWithFilters();
    
    // Since we load all articles at once, just update pagination
    this.updatePaginatedArticles();
    this.scrollToTop();
  }
  
  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }
  
  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }
  
  nextPageGroup(): void {
    const newStart = this.pageGroupStart + this.pagesPerGroup;
    if (newStart <= this.totalPages) {
      this.pageGroupStart = newStart;
      this.currentPage = newStart;
      this.updatePageNumbers();
      this.updatePaginatedArticles();
      this.updateUrlWithFilters();
      this.scrollToTop();
    }
  }
  
  prevPageGroup(): void {
    const newStart = this.pageGroupStart - this.pagesPerGroup;
    if (newStart >= 1) {
      this.pageGroupStart = newStart;
      this.currentPage = newStart;
      this.updatePageNumbers();
      this.updatePaginatedArticles();
      this.updateUrlWithFilters();
      this.scrollToTop();
    }
  }
  
  /**
   * Build URL for page link (for SEO)
   */
  buildPageUrl(page: number): string {
    if (!this.updateUrl) return '#';
    
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('page', page.toString());
    
    // Preserve active filters in URL
    if (this.selectedFilters.discipline !== 'All') {
      queryParams.set('discipline', this.selectedFilters.discipline);
    } else {
      queryParams.delete('discipline');
    }
    
    if (this.selectedFilters.subject !== 'All') {
      queryParams.set('subject', this.selectedFilters.subject);
    } else {
      queryParams.delete('subject');
    }
    
    if (this.selectedFilters.topic !== 'All') {
      queryParams.set('topic', this.selectedFilters.topic);
    } else {
      queryParams.delete('topic');
    }
    
    if (this.selectedFilters.theme !== 'All') {
      queryParams.set('theme', this.selectedFilters.theme);
    } else {
      queryParams.delete('theme');
    }
    
    if (this.selectedFilters.contentType !== 'All') {
      queryParams.set('contentType', this.selectedFilters.contentType);
    } else {
      queryParams.delete('contentType');
    }
    
    if (this.selectedFilters.contentSeries !== 'All') {
      queryParams.set('contentSeries', this.selectedFilters.contentSeries);
    } else {
      queryParams.delete('contentSeries');
    }
    
    if (this.selectedFilters.product !== 'All') {
      queryParams.set('product', this.selectedFilters.product);
    } else {
      queryParams.delete('product');
    }
    
    return `${window.location.pathname}?${queryParams.toString()}`;
  }
  
  private updatePageNumbers(): void {
    const start = this.pageGroupStart;
    const end = Math.min(start + this.pagesPerGroup - 1, this.totalPages);
    
    this.pageNumbers = [];
    for (let i = start; i <= end; i++) {
      this.pageNumbers.push(i);
    }
  }
  
  private resetPagination(): void {
    this.currentPage = 1;
    this.pageGroupStart = 1;
    this.totalCount = 0;
    this.totalPages = 0;
    this.pageNumbers = [];
  }
  
  private scrollToTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
  
  // Getters for template
  get canGoNext(): boolean {
    return this.currentPage < this.totalPages;
  }
  
  get canGoPrev(): boolean {
    return this.currentPage > 1;
  }
  
  get canGoNextGroup(): boolean {
    return this.pageGroupStart + this.pagesPerGroup <= this.totalPages;
  }
  
  get canGoPrevGroup(): boolean {
    return this.pageGroupStart > 1;
  }
  
  get hasArticles(): boolean {
    return this.articles.length > 0;
  }
  
  get showPagination(): boolean {
    return this.totalPages > 1 && !this.loading;
  }
  
  /**
   * Track by function for ngFor performance
   */
  trackByArticleId(index: number, article: ArticleCardData): string {
    return article.id;
  }
}

